import Loader from "@components/Loader";
import { FormikProvider } from "formik";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import FormikInput from "@components/FormikInput";
import FormikCountryInput from "@components/FormikCountryInput";
import FormikCityInput from "@components/FormikCityInput";
import React, { useEffect, useState } from "react";
import { ReactComponent as IconInfo } from "@assets/ic-info.svg";

import { useService } from "@/base/hooks/useService";
import { LocationsListsService } from "@services/LocationsListsService";

import styles from "@components/AddCardCCBill/index.module.scss";

const CCBillCardForm = ({
  formik,
  formRef,
  isLoading,
  isOpen,
  onSettings,
  setStateSelectEmpty,
  id,
  withoutTooltip,
  amount = "0.01",
  ipAddress,
}) => {
  const { t } = useTranslation("modals");

  const locationsListsService = useService(LocationsListsService);

  const [countries, setCountries] = useState([]);
  const getCountries = async () => {
    try {
      const { data } = await locationsListsService.getCountries();
      setCountries(data);
    } finally {
    }
  };

  useEffect(() => {
    void getCountries();
  }, []);

  return (
    <div className="mb-3 position-relative">
      {isLoading && <Loader className={styles.loader} />}
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} id={id} ref={formRef}>
          <span className="text-primary subtitle-2 mb-2 d-block">
            {t("ADD_CARD_FIRST_TITLE")}
          </span>

          <div className={classNames(styles.inputWrapper, "d-flex")}>
            <FormikInput
              label="FIRST_NAME"
              type="text"
              name="firstName"
              placeholder="FIRST_NAME"
              dataCCBill="firstName"
              wrapperClassName={styles.input}
            />
            <FormikInput
              label="LAST_NAME"
              type="text"
              name="lastName"
              placeholder="LAST_NAME"
              dataCCBill="lastName"
              wrapperClassName={styles.input}
            />
          </div>

          <div className={classNames(styles.inputWrapper, "d-flex")}>
            <FormikCountryInput
              label="COUNTRY_OR_REGION"
              placeholder="SELECT_COUNTRY_OR_REGION"
              countryList={countries}
              name="country"
              wrapperClassName={styles.input}
            />
            <FormikCityInput
              countryCode={formik.values.countryCode}
              wrapperClassName={styles.input}
              name="stateFull"
              secondName="state"
              setIsEmpty={setStateSelectEmpty}
              label="STATE_OR_PROVINCE"
              placeholder="SELECT_STATE_OR_PROVINCE"
            />
          </div>

          <div className={classNames(styles.inputWrapper, "d-flex")}>
            <FormikInput
              label="CITY"
              type="text"
              name="city"
              placeholder="ENTER_CITY"
              dataCCBill="city"
              wrapperClassName={styles.input}
            />
            <FormikInput
              label="ADDRESS"
              type="text"
              name="address1"
              placeholder="ADDRESS"
              dataCCBill="address1"
              wrapperClassName={styles.input}
            />
          </div>
          <div className={classNames(styles.inputWrapper, "d-flex")}>
            <div className={styles.input}>
              <FormikInput
                label="ZIP"
                type="text"
                name="postalCode"
                placeholder="ENTER_ZIP"
                dataCCBill="postalCode"
                wrapperClassName={classNames(styles.input, "pb-0")}
                contentStyles={styles.zipTooltip}
              >
                {!(formik.errors.postalCode && formik.touched.postalCode) ? (
                  <div className={classNames("px-2")}>
                    <span className="caption tooltip-grey">
                      {t("ADD_CARD_ZIP_TOOLTIP")}
                    </span>
                  </div>
                ) : null}
              </FormikInput>
            </div>

            <FormikInput
              label="EMAIL"
              type="text"
              name="email"
              placeholder="ENTER_EMAIL"
              dataCCBill="email"
              wrapperClassName={styles.input}
            />
          </div>

          <span className="text-primary subtitle-2 mb-2 d-block">
            {t("ADD_CARD_SECOND_TITLE")}
          </span>
          <div className={classNames(styles.inputWrapper, "d-flex")}>
            <FormikInput
              label="CARD_NUMBER"
              type="text"
              name="cardNumber"
              placeholder="XXXX XXXX XXXX XXXX"
              mask="9999 9999 9999 9999"
              formatChars={{
                9: "[0-9]",
              }}
              wrapperClassName={styles.input}
            />
            <FormikInput
              label="CVV"
              type="password"
              name="cvv2"
              mask="999"
              placeholder="CVV"
              dataCCBill="cvv2"
              wrapperClassName={styles.input}
            />
          </div>

          <div className={classNames(styles.inputWrapper, "d-flex")}>
            <FormikInput
              label="EXPIRATION_MONTH"
              type="text"
              name="expirationMonth"
              placeholder="MM"
              mask="99"
              wrapperClassName={styles.input}
            />
            <FormikInput
              label="EXPIRATION_YEAR"
              name="expirationYear"
              type="text"
              placeholder="YYYY"
              mask="9999"
              wrapperClassName={styles.input}
            />
          </div>

          {!withoutTooltip ? (
            <div className={styles.infoContainer}>
              <IconInfo className={classNames(styles.infoBadge)} />
              <div>
                <span className="d-block text-body-2 text-tertiary">
                  {t("ADD_CARD_FIRST_TEXT")}
                </span>
                {!onSettings && (
                  <span className="d-block text-body-2 text-tertiary">
                    {t("ADD_CARD_SECOND_TEXT")}
                  </span>
                )}
              </div>
            </div>
          ) : null}
          {isOpen && (
            <div className="d-none">
              <input
                type="text"
                name="cardNumber"
                data-ccbill="cardNumber"
                value={formik.values.cardNumber.replace(/ /g, "")}
                placeholder="cardNumber"
              />
              <input
                type="text"
                name="country"
                data-ccbill="country"
                value={formik.values.countryCode}
                placeholder="country"
              />
              <input
                type="text"
                name="state"
                data-ccbill="state"
                value={formik.values.state}
                placeholder="state"
              />
              <input
                type="text"
                name="currencyCode"
                placeholder="currencyCode"
                data-ccbill="currencyCode"
                value="840"
              />
              <input
                type="number"
                name="amount"
                placeholder="amount"
                data-ccbill="amount"
                value={amount}
              />
              <input
                type="string"
                name="expYear"
                placeholder="expYear"
                data-ccbill="expYear"
                value={formik.values.expirationYear}
              />
              <input
                type="string"
                name="expMonth"
                placeholder="expMonth"
                data-ccbill="expMonth"
                value={formik.values.expirationMonth}
              />
              <input
                type="string"
                name="ipAddress"
                placeholder="ipAddress"
                data-ccbill="ipAddress"
                value={ipAddress ? ipAddress : formik.values.ipAddress}
              />
              <input
                type="string"
                name="email"
                placeholder="email"
                data-ccbill="email"
                value={formik?.values?.email}
              />
              <input
                type="text"
                name="nameOnCard"
                placeholder="Name on Card"
                data-ccbill="nameOnCard"
                value={`${formik.values.firstName} ${formik.values.lastName}`}
              />
            </div>
          )}
        </form>
      </FormikProvider>
    </div>
  );
};

export default CCBillCardForm;
